<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { RotateCcw } from 'lucide-vue-next'
import DialogRestoreDefaults from './dialogs/DialogRestoreDefaults.vue'

const store = useStore()

const uiOptions = computed(() => store.getters['configModule/uiOptions'] || {})
const uiOverrides = computed(() => store.getters['configModule/uiOverrides'] || {})

const emit = defineEmits(['configChange'])

const optionsList = [
  { key: 'insightEnabled', label: 'Insight Mode', description: 'Enable market typeahead / insights and AI-powered input' },
  { key: 'speakEnabled', label: 'Voice Controls', description: 'Allow voice commands for bet building (Not currently supported on native)' },
  { key: 'betSlipAsImageEnabled', label: 'Betslip Image Mode', description: 'Generate images for the betslip (as bet is edited)' },
  { key: 'shareAttachImage', label: 'Attach Image', description: 'Attach images to share payloads (not just link previews)' },
  { key: 'linkToShareImage', label: 'Direct Image Link', description: 'Link to shared bet image to either return to bet or go to bookmaker' },
  { key: 'shareImagePreview', label: 'Image Preview', description: 'Show preview of shared images to select design as sharing' },
  { key: 'autoQueryEnabled', label: 'Auto Query', description: 'Refresh betslip when typing pauses (if not using insight)' },
  { key: 'betSlipViewerCopyBetEnabled', label: 'Copy Bet (Bundle Viewer)', description: 'Enable copy bet on share dialog for bundle viewer' },
  { key: 'showAllContexts', label: 'Show All Contexts (With Name)', description: 'Contexts will include canned and snapshots with contexts names (Intended DEV only))' },
]

function handleToggle(key: string, currentValue: boolean) {
  store.dispatch('configModule/setUIOverride', { key, value: !currentValue })
  emit('configChange')
}

const showRestoreDialog = ref(false)

function onRestoreConfirm() {
  showRestoreDialog.value = false
  store.dispatch('configModule/resetUIOverrides')
  emit('configChange')
}

function restoreDefaults() {
  showRestoreDialog.value = true
}

function isOverridden(key: string) {
  return key in uiOverrides.value
}
</script>

<template>
  <div class="flex flex-col gap-8">
    <div class="space-y-6">
      <div v-for="opt in optionsList" :key="opt.key" class="flex items-center justify-between p-4 rounded-xl bg-white dark:bg-white/5 border border-gray-100 dark:border-white/5 transition-all">
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <Label :for="opt.key" class="text-sm font-bold">{{ opt.label }}</Label>
            <span v-if="isOverridden(opt.key)" class="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded font-bold uppercase tracking-wider">Override</span>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400">{{ opt.description }}</p>
        </div>
        
        <Switch 
          :id="opt.key"
          :checked="uiOptions[opt.key]"
          @update:checked="handleToggle(opt.key, uiOptions[opt.key])"
        />
      </div>
    </div>

    <div class="pt-6 border-t border-gray-100 dark:border-white/5">
      <Button 
        variant="outline" 
        class="w-full h-12 gap-2 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 border-red-100 dark:border-red-900/30 rounded-xl transition-all"
        @click="restoreDefaults"
      >
        <RotateCcw class="w-4 h-4" />
        Restore Defaults
      </Button>
    </div>
  </div>

  <DialogRestoreDefaults 
    :open="showRestoreDialog"
    @cancel="showRestoreDialog = false"
    @confirm="onRestoreConfirm"
  />
</template>
